import React, { useState, useEffect } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { AdminDisputes } from '../components/AdminDisputes';
import AdminFinance from '../components/AdminFinance';
import AdminReports from '../components/AdminReports';
import { PlatformIcon, platformLabel, profileUrl } from '../utils/platformIcons';

/* The follower bot's failure codes, mapped to the strings the approver reads.
   Every failure the endpoint can report is named here on purpose: a raw code
   leaking into the panel tells the person nothing about whether they now need
   to go open the profile themselves. */
const LOOKUP_ERROR_KEYS = {
    no_public_count: 'botErrNoPublicCount',
    unsupported: 'botErrUnsupported',
    not_found: 'botErrNotFound',
    blocked: 'botErrBlocked',
    rate_limited: 'botErrRateLimited',
    network: 'botErrNetwork',
    unreadable: 'botErrUnreadable',
    no_username: 'botErrNoUsername',
    not_in_queue: 'botErrNotInQueue',
    request_failed: 'botErrRequestFailed',
    internal: 'botErrInternal',
};

// Matches Admin_social_followers.MAX_BATCH on the server.
const LOOKUP_CHUNK = 20;

function Admin() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("Contact");
    const [text, setText] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const response = await api.get("/api/admin/data/");
            setData(response.data);
        } catch (error) {
            console.error(error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const tabs = [
        { key: "Contact", label: t('admin.tabContact') },
        { key: "PromoCode", label: t('admin.tabPromo') },
        { key: "AddUser", label: t('admin.tabAddUser') },
        { key: "Users", label: t('admin.tabUsers') },
        { key: "ManuelSocial", label: t('admin.tabManuelSocial') || "Manual Social Requests" },
        { key: "Socials", label: t('admin.tabSocials') || "Manage Socials" },
        { key: "Finance", label: t('admin.tabFinance') },
        { key: "Disputes", label: t('admin.tabDisputes') },
        { key: "Reports", label: t('admin.tabReports') },
    ];

    function Contact() {
        const handleDelete = async (id) => {
            try {
                await api.delete(`/api/admin/delete-contact/${id}`);
                setData({ ...data, contacts: data.contacts.filter((item) => item.id !== id) });
            } catch (error) { alert("error"); }
        }

        return (
            <div className="space-y-3">
                {data.contacts.map((item, index) => (
                    <div key={index} className="card">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-slate-800">{item.name}</h3>
                                <p className="text-xs text-slate-500">{item.email}</p>
                                <p className="text-xs text-slate-500">{item.subject}</p>
                                <p className="text-xs text-slate-400">{item.user}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs text-slate-400">{item.date}</span>
                                <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 transition">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <p className="text-sm text-slate-700 mt-2">{item.message}</p>
                    </div>
                ))}
            </div>
        );
    }

    function PromoCode() {
        const [name, Setname] = useState("");
        const [expDate, SetexpDate] = useState("2030-01-01");
        const [amount, Setamount] = useState(1);
        const [percent, Setpercent] = useState("");
        const [copied, setCopied] = useState(false);

        const handleDelete = async (id) => {
            try {
                await api.delete(`/api/admin/delete-promo/${id}`);
                setData({ ...data, promo_codes: data.promo_codes.filter((item) => item.id !== id) });
            } catch (error) { }
        }

        const handleSubmit = async () => {
            try {
                const res = await api.post("/api/admin/create-promo/", { name, expDate, amount, percent });
                if (res.status === 200) {
                    setData({ ...data, promo_codes: [...data.promo_codes, res.data] });
                    Setname(""); SetexpDate("2030-01-01"); Setamount(1); Setpercent("");
                }
            } catch (error) { alert("error"); }
        }

        const updatetext = (code) => {
            return text.replace(/\*/g, code);
        };

        return (
            <div className="space-y-6">
                {/* Template */}
                <div className="card">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">{t('admin.promoTemplateTitle')}</h3>
                    <textarea value={text} placeholder={t('admin.promoTemplatePlaceholder')} onChange={(e) => setText(e.target.value)} rows={3} className="input min-h-[80px] resize-y" />
                    <button onClick={() => setText("")} className="btn-secondary text-sm mt-2">{t('admin.clearBtn')}</button>
                </div>

                {/* Create form */}
                <div className="card">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('admin.createPromoTitle')}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <input type="text" className="input" onChange={(e) => Setname(e.target.value)} placeholder={t('admin.promoNamePlaceholder')} value={name} />
                        <input type="date" className="input" onChange={(e) => SetexpDate(e.target.value)} value={expDate} />
                        <input type="number" className="input" onChange={(e) => Setamount(e.target.value)} placeholder={t('admin.promoAmountPlaceholder')} value={amount} />
                    </div>
                    <button onClick={handleSubmit} className="btn-primary text-sm mt-3">{t('admin.addBtn')}</button>
                </div>

                {/* List */}
                <div className="space-y-3">
                    {data.promo_codes.slice().reverse().map((item, index) => (
                        <div key={index} className="card">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-semibold text-slate-800 cursor-pointer hover:text-primary-600 transition"
                                        onClick={() => {
                                            navigator.clipboard.writeText(updatetext(item.code)).then(() => {
                                                setCopied(true); setTimeout(() => setCopied(false), 2000);
                                            });
                                        }}
                                    >
                                        {item.code}
                                        {copied && <span className="ml-2 text-emerald-500 text-xs">{t('admin.copied')}</span>}
                                    </h4>
                                    <p className="text-xs text-slate-500">{item.email}</p>
                                    <p className="text-xs text-slate-400">{t('admin.percent')}: {item.percent} | {t('admin.amount')}: {item.amount}</p>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-xs text-slate-400">{item.exprial_date}</span>
                                    <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700 transition">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    function AddUser() {
        const [username, setUsername] = useState("");
        const [email, setEmail] = useState("");
        const [newUser, setNewUser] = useState({});

        const handleUpdate = async () => {
            try {
                const res = await api.post(`/api/admin/add-user/`, { username, email });
                setNewUser(res.data);
            } catch (error) { alert("error"); }
        }

        return (
            <div className="space-y-4">
                <div className="card">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('admin.createUserTitle')}</h3>
                    <div className="flex gap-3 flex-wrap">
                        <input type="text" className="input flex-1" onChange={(e) => setUsername(e.target.value)} placeholder={t('admin.usernamePlaceholder')} value={username} />
                        <input type="email" className="input flex-1" onChange={(e) => setEmail(e.target.value)} placeholder={t('admin.emailPlaceholder')} value={email} />
                        <button onClick={handleUpdate} className="btn-primary text-sm">{t('admin.addBtn')}</button>
                    </div>
                </div>
                {newUser && newUser.username && (
                    <div className="card bg-emerald-50 border border-emerald-200">
                        <p className="text-sm"><span className="text-slate-500">{t('admin.usernameLabel')}:</span> <span className="font-medium text-slate-800">{newUser.username}</span></p>
                        <p className="text-sm"><span className="text-slate-500">{t('admin.emailLabel')}:</span> <span className="font-medium text-slate-800">{newUser.email}</span></p>
                        <p className="text-sm"><span className="text-slate-500">{t('admin.passwordLabel')}:</span> <span className="font-mono font-medium text-slate-800">{newUser.password}</span></p>
                    </div>
                )}
            </div>
        );
    }

    function Users() {
        const users = data.users || [];
        const verification_requests = data.verification_requests || [];

        const handleResetStar = async (id) => {
            try {
                await api.post('/api/admin/reset-star/', { user_id: id });
                setData(prev => ({
                    ...prev,
                    users: prev.users.map(u => u.id === id ? { ...u, stars: 0 } : u)
                }));
            } catch (error) {
                // console.error("Error resetting star count", error);
            }
        };

        const handleTrial = async (id, event) => {
            try {
                const res = await api.post('/api/admin/end-trial/', { id: id, event: event });
                if (res.status === 200) {
                    setData(prev => ({
                        ...prev,
                        users: prev.users.map(u => u.id === id ? { ...u, trial: event === 'start' } : u)
                    }));
                }
            } catch (error) {
                // console.error("Error updating trial", error);
            }
        };

        const handlebranToggle = async (id) => {
            try {
                const res = await api.post('/api/admin/manage-brand/', { id: id });
                if (res.status === 200) {
                    setData(prev => ({
                        ...prev,
                        users: prev.users.map(u => u.id === id ? { ...u, is_brand: res.data } : u)
                    }));
                }
            } catch (error) {
                // console.error("Error updating brand", error);
            }
        };

        const handleInfluencerToggle = async (id) => {
            try {
                const res = await api.post('/api/admin/manage-influencer/', { id: id });
                if (res.status === 200) {
                    setData(prev => ({
                        ...prev,
                        users: prev.users.map(u => u.id === id ? { ...u, is_content_producer: res.data } : u)
                    }));
                }
            } catch (error) {
                // console.error("Error updating influencer", error);
            }
        };

        const handleVerifiedToggle = async (id) => {
            try {
                const res = await api.post('/api/admin/manage-verified/', { id: id });
                if (res.status === 200) {
                    setData(prev => ({
                        ...prev,
                        users: prev.users.map(u => u.id === id ? { ...u, verified: res.data } : u)
                    }));
                }
            } catch (error) {
                // console.error("Error updating verified status", error);
            }
        };

        const usersWithStars = users.filter(u => u.stars && u.stars !== 0).sort((a, b) => b.stars - a.stars);
        const usersWithoutStars = users.filter(u => !u.stars || u.stars === 0).sort((a, b) => b.id - a.id);

        const renderUserSection = (userList, titleKey) => {
            if (userList.length === 0) return null;
            return (
                <div className="space-y-4 mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-700">{t(titleKey)}</h3>
                        <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium">{userList.length} {t('admin.total')}</span>
                    </div>

                    {/* Desktop table */}
                    <div className="hidden sm:block card overflow-x-auto p-0">
                        <table className="w-full text-sm min-w-[800px]">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50">
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">{t('admin.userId')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('admin.userUsername')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('admin.userEmail')}</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Joined</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Trial</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Creator</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Brand</th>
                                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Verified</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('admin.userStars')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {userList.map((u) => (
                                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3 text-slate-400 font-mono text-xs">{u.id}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                                    {u.username.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="font-medium text-slate-800">{u.username}</span>
                                                {u.third_party && (
                                                    <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">{t('admin.thirdParty')}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">{u.email || <span className="text-slate-300 italic">—</span>}</td>
                                        <td className="px-4 py-3 text-slate-500 text-xs text-center">
                                            {u.date_joined ? new Date(u.date_joined).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {u.trial ? (
                                                <button onClick={() => handleTrial(u.id, 'end')} className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded hover:bg-red-100 transition whitespace-nowrap">End Trial</button>
                                            ) : (
                                                <button onClick={() => handleTrial(u.id, 'start')} className="px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-600 rounded hover:bg-emerald-100 transition whitespace-nowrap">Start Trial</button>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => handleInfluencerToggle(u.id)} className={`w-9 h-5 rounded-full inline-flex items-center transition px-0.5 ${u.is_content_producer ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Content Producer">
                                                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.is_content_producer ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => handlebranToggle(u.id)} className={`w-9 h-5 rounded-full inline-flex items-center transition px-0.5 ${u.is_brand ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Brand">
                                                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.is_brand ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => handleVerifiedToggle(u.id)} className={`w-9 h-5 rounded-full inline-flex items-center transition px-0.5 ${u.verified ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Verified">
                                                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.verified ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${u.stars > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                                                    }`}>
                                                    🍩 {u.stars}
                                                </span>
                                                <button onClick={() => handleResetStar(u.id)} className="text-slate-400 hover:text-red-500 transition" title="Reset Stars">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden space-y-3">
                        {userList.map((u) => (
                            <div key={u.id} className="card">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                        {u.username.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-slate-800 text-sm">{u.username}</span>
                                            {u.third_party && (
                                                <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">{t('admin.thirdParty')}</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 truncate">{u.email || '—'}</p>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${u.stars > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'
                                            }`}>🍩 {u.stars}</span>
                                        <button onClick={() => handleResetStar(u.id)} className="text-slate-400 hover:text-red-500 transition text-xs flex items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100">
                                    <div className="flex flex-col gap-1 text-left">
                                        <p className="text-[11px] text-slate-400 font-mono"><span className="text-slate-500">ID:</span> {u.id}</p>
                                        <p className="text-[11px] text-slate-400"><span className="text-slate-500">Joined:</span> {u.date_joined ? new Date(u.date_joined).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</p>
                                    </div>
                                    <div className="flex flex-row items-center gap-3">
                                        <button onClick={() => handleInfluencerToggle(u.id)} className={`w-9 h-5 rounded-full flex items-center transition px-0.5 shrink-0 ${u.is_content_producer ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Content Producer">
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.is_content_producer ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                        </button>
                                        <button onClick={() => handlebranToggle(u.id)} className={`w-9 h-5 rounded-full flex items-center transition px-0.5 shrink-0 ${u.is_brand ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Brand">
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.is_brand ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                        </button>
                                        <button onClick={() => handleVerifiedToggle(u.id)} className={`w-9 h-5 rounded-full flex items-center transition px-0.5 shrink-0 ${u.verified ? 'bg-primary-600' : 'bg-slate-200'}`} title="Toggle Verified">
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${u.verified ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                        </button>
                                        <div>
                                            {u.trial ? (
                                                <button onClick={() => handleTrial(u.id, 'end')} className="px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition shadow-sm">End Trial</button>
                                            ) : (
                                                <button onClick={() => handleTrial(u.id, 'start')} className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-600 rounded-md hover:bg-emerald-100 transition shadow-sm">Start Trial</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        };

        const renderVerificationRequests = (requests) => {
            if (requests.length === 0) return null;
            return (
                <div className="space-y-4 mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-semibold text-slate-700">{t('admin.verificationRequests') || 'Verification Requests'}</h3>
                        <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium">{requests.length} {t('admin.total')}</span>
                    </div>

                    <div className="card overflow-x-auto p-0">
                        <table className="w-full text-sm min-w-[500px]">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50">
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">ID</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {requests.map((r) => (
                                    <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3 text-slate-400 font-mono text-xs">{r.id}</td>
                                        <td className="px-4 py-3 font-medium text-slate-800">{r.user}</td>
                                        <td className="px-4 py-3 text-slate-500">
                                            {r.date ? new Date(r.date).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        };

        return (
            <div>
                {renderVerificationRequests(verification_requests)}
                {renderUserSection(usersWithStars, 'admin.usersWithStars')}
                {renderUserSection(usersWithoutStars, 'admin.usersWithoutStars')}
            </div>
        );
    }

    function ManuelSocial() {
        const [followersMap, setFollowersMap] = useState({});
        /* What the follower bot read, keyed by request id. It stays beside the
           input rather than inside it: once a number sits in the field there is
           no way to tell a figure that was read off the live profile from one
           somebody typed, and that difference is the whole point of this queue. */
        const [lookups, setLookups] = useState({});
        const [busy, setBusy] = useState({});
        const [bulkBusy, setBulkBusy] = useState(false);

        const handleDecline = async (id) => {
            try {
                await api.delete(`/api/admin/manuel_social_media/`, { data: { id } });
                setData({ ...data, manule_socials: data.manule_socials.filter((item) => item.id !== id) });
            } catch (error) { alert("error"); }
        };

        const handleAccept = async (id) => {
            try {
                const followers = followersMap[id] || 0;
                await api.post(`/api/admin/manuel_social_media/`, { id, followers });
                setData({ ...data, manule_socials: data.manule_socials.filter((item) => item.id !== id) });
            } catch (error) { alert("error"); }
        }

        /* Built from the shared table rather than five `if`s, so the preview
           resolves for every platform the profile picker offers — an approver
           who cannot open the account has no way to check it before saying
           yes. `profileUrl` matches the backend's `social_profile_url`. */
        const items = data.manule_socials || [];

        const applyResults = (results) => {
            const nextFollowers = {};
            const nextLookups = {};
            results.forEach((entry) => {
                nextLookups[entry.id] = entry;
                if (entry.ok && entry.followers !== null && entry.followers !== undefined) {
                    nextFollowers[entry.id] = String(entry.followers);
                }
            });
            /* Only rows the bot could actually read get their input written.
               A failed lookup leaves whatever the approver already typed alone
               — blanking it would quietly throw away their work. */
            setFollowersMap((prev) => ({ ...prev, ...nextFollowers }));
            setLookups((prev) => ({ ...prev, ...nextLookups }));
        };

        const runLookup = async (ids) => {
            if (!ids.length) return;
            // The endpoint fetches one profile per id and caps a call at 20, so
            // a full queue goes over in chunks instead of one long request that
            // a gateway would cut off half-read.
            for (let i = 0; i < ids.length; i += LOOKUP_CHUNK) {
                const chunk = ids.slice(i, i + LOOKUP_CHUNK);
                try {
                    const response = await api.post('/api/admin/social-followers/', { ids: chunk });
                    applyResults(response.data.results || []);
                } catch (error) {
                    const failed = {};
                    chunk.forEach((id) => { failed[id] = { id, ok: false, error: 'request_failed' }; });
                    setLookups((prev) => ({ ...prev, ...failed }));
                }
            }
        };

        const handleFetchOne = async (id) => {
            setBusy((prev) => ({ ...prev, [id]: true }));
            try {
                await runLookup([id]);
            } finally {
                setBusy((prev) => ({ ...prev, [id]: false }));
            }
        };

        const handleFetchAll = async () => {
            setBulkBusy(true);
            try {
                await runLookup(items.map((item) => item.id));
            } finally {
                setBulkBusy(false);
            }
        };

        const lookupNote = (entry) => {
            if (!entry) return null;
            if (entry.ok) {
                const count = Number(entry.followers).toLocaleString();
                return { tone: 'text-emerald-600', text: `${t('admin.botRead')}: ${count} · ${entry.source}` };
            }
            return { tone: 'text-amber-600', text: t(`admin.${LOOKUP_ERROR_KEYS[entry.error] || 'botErrUnknown'}`) };
        };

        return (
            <div className="space-y-4">
                {items.length > 0 && (
                    <div className="card flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">{t('admin.botHint')}</p>
                        <button
                            onClick={handleFetchAll}
                            disabled={bulkBusy}
                            className="btn-secondary py-1.5 text-xs whitespace-nowrap disabled:opacity-60"
                        >
                            {bulkBusy ? t('admin.botWorking') : t('admin.botFetchAll')}
                        </button>
                    </div>
                )}
                {items.length === 0 && <div className="text-sm text-slate-500">No requests found.</div>}
                {items.map((item, index) => {
                    const note = lookupNote(lookups[item.id]);
                    return (
                    <div key={index} className="card">
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
                            <div className="space-y-1">
                                <h3 className="text-sm font-semibold text-slate-800">{item.name}</h3>
                                <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                    {t('admin.platformLabel')}:
                                    <PlatformIcon platform={item.social_media} size={16} />
                                    <span className="font-medium text-slate-700">{platformLabel(item.social_media)}</span>
                                </p>
                                <p className="text-xs text-slate-500">{t('admin.usernameLabel')}: <span className="font-medium text-slate-700">{item.username}</span></p>
                                <div className="pt-1">
                                    <a href={profileUrl(item.social_media, item.username)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">
                                        {t('admin.linkLabel')} ↗
                                    </a>
                                </div>
                                {item.message && <p className="text-xs text-slate-600 mt-2 bg-slate-50 p-2 rounded-md">{item.message}</p>}
                            </div>
                            <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <input
                                        className="input w-full sm:w-32 py-1.5 text-sm"
                                        placeholder={t('admin.followersLabel')}
                                        value={followersMap[item.id] || ''}
                                        onChange={(e) => setFollowersMap({ ...followersMap, [item.id]: e.target.value })}
                                        type="number"
                                    />
                                    <button
                                        onClick={() => handleFetchOne(item.id)}
                                        disabled={busy[item.id] || bulkBusy}
                                        title={t('admin.botHint')}
                                        className="btn-secondary py-1.5 px-2 text-xs whitespace-nowrap disabled:opacity-60"
                                    >
                                        {busy[item.id] ? t('admin.botWorking') : t('admin.botFetch')}
                                    </button>
                                </div>
                                {note && <p className={`text-[11px] text-right max-w-[16rem] ${note.tone}`}>{note.text}</p>}
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button onClick={() => handleDecline(item.id)} className="btn-secondary w-full sm:w-auto py-1.5 text-xs text-red-600 hover:bg-red-50 hover:border-red-200">
                                        {t('admin.declineBtn')}
                                    </button>
                                    <button onClick={() => handleAccept(item.id)} className="btn-primary w-full sm:w-auto py-1.5 text-xs bg-emerald-500 hover:bg-emerald-600 focus:ring-emerald-200 border-none">
                                        {t('admin.acceptBtn')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>
        );
    }

    function Socials() {
        const [followersMap, setFollowersMap] = useState({});

        const handleUpdate = async (id) => {
            try {
                const followers = followersMap[id] || 0;
                await api.put(`/api/admin/update_socials/`, { id, followers });
                setData({ ...data, socials: data.socials.filter((item) => item.id !== id) });
            } catch (error) { alert("error"); }
        }

        const handleDelete = async (id) => {
            try {
                await api.post(`/api/admin/update_socials/`, { id });
                setData({ ...data, socials: data.socials.filter((item) => item.id !== id) });
            } catch (error) { alert("error"); }
        }

        const items = data.socials || [];

        return (
            <div className="space-y-4">
                {items.length === 0 && <div className="text-sm text-slate-500">No socials found.</div>}
                {items.map((item, index) => (
                    <div key={index} className="card">
                        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
                            <div className="space-y-1">
                                <h3 className="text-sm font-semibold text-slate-800">{item.username}</h3>
                                <p className="text-xs text-slate-500">{t('admin.followersLabel')}: <span className="font-medium text-slate-700">{item.followers}</span></p>
                                <p className="text-xs text-slate-500">{t('admin.updateDateLabel')}: <span className="font-medium text-slate-700">{item.update_date}</span></p>
                                <div className="pt-1">
                                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline break-all">
                                        {item.link} ↗
                                    </a>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                                <input 
                                    className="input w-full sm:w-32 py-1.5 text-sm" 
                                    placeholder={t('admin.followersLabel')}
                                    value={followersMap[item.id] || ''}
                                    onChange={(e) => setFollowersMap({ ...followersMap, [item.id]: e.target.value })} 
                                    type="number" 
                                />
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button onClick={() => handleDelete(item.id)} className="btn-secondary w-full sm:w-auto py-1.5 text-xs text-red-600 hover:bg-red-50 hover:border-red-200">
                                        {t('admin.deleteBtn')}
                                    </button>
                                    <button onClick={() => handleUpdate(item.id)} className="btn-primary w-full sm:w-auto py-1.5 text-xs">
                                        {t('admin.updateBtn')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get("/api/admin/data/");
                setData(response.data);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return <div className="flex items-center justify-center min-h-screen"><div className="spinner"></div></div>;
    }
    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-2">{t('admin.unauthorizedTitle')}</h1>
                    <p className="text-slate-500">{t('admin.unauthorizedMsg')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-24">
            {/* Mobile header */}
            <div className="md:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <h1 className="text-lg font-semibold text-slate-800">{t('admin.headerTitle')}</h1>
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 md:hidden" onClick={() => setSidebarOpen(false)}>
                    <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
                    <div className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl p-6" onClick={e => e.stopPropagation()}>
                        <h2 className="text-lg font-bold text-slate-800 mb-6">{t('admin.headerTitle')}</h2>
                        <nav className="space-y-1">
                            {tabs.map(t => (
                                <button key={t.key} onClick={() => { setActiveTab(t.key); setSidebarOpen(false); }}
                                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition
                                        ${activeTab === t.key ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-100'}`}
                                >{t.label}</button>
                            ))}
                        </nav>
                        <div className="mt-8 border-t border-slate-100 pt-6">
                            <button onClick={handleRefresh} disabled={isRefreshing} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition disabled:opacity-50 shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {t('admin.refreshBtn') || 'Refresh'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-[1400px] mx-auto flex gap-4 sm:gap-8 px-4 pt-6">
                {/* Desktop sidebar */}
                <aside className="hidden md:block w-44 shrink-0">
                    <div className="sticky top-6">
                        <h1 className="text-xl font-bold text-slate-800 mb-6 pl-1">{t('admin.headerTitle')}</h1>
                        <nav className="space-y-1">
                            {tabs.map(t => (
                                <button key={t.key} onClick={() => setActiveTab(t.key)}
                                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition
                                        ${activeTab === t.key ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-100'}`}
                                >{t.label}</button>
                            ))}
                        </nav>
                        <div className="mt-8 pt-6 border-t border-slate-200/60 mr-4">
                            <button onClick={handleRefresh} disabled={isRefreshing} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold transition disabled:opacity-50 shadow-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {t('admin.refreshBtn') || 'Refresh'}
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Content */}
                <main className="flex-1 min-w-0">
                    {activeTab === 'Contact' && <Contact />}
                    {activeTab === 'PromoCode' && <PromoCode />}
                    {activeTab === 'AddUser' && <AddUser />}
                    {activeTab === 'Users' && <Users />}
                    {activeTab === 'ManuelSocial' && <ManuelSocial />}
                    {activeTab === 'Socials' && <Socials />}
                    {activeTab === 'Finance' && <AdminFinance />}
                    {activeTab === 'Disputes' && <AdminDisputes />}
                    {activeTab === 'Reports' && <AdminReports />}
                </main>
            </div>
        </div>
    );
}
export default Admin;
